import { useState } from "react";

import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Guid } from "@/Common/Models/Guid";
import { Person } from "@/Common/Models/Person/Person";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { FormMode } from "@/Common/Models/UI/Enums";
import { UsersListFieldComponent } from "./UsersListFieldComponent";

import styles from "./AddEmployeeProtectMedicalNotePopup.module.scss";

interface AddEmployeeProtectMedicalNotePopupProps {
    onSave: (contactFacetIds: Guid[]) => void
    contactFacetIds: Guid[];
    onClose: () => void;
    dataCy?: string;
}

function AddEmployeeProtectMedicalNotePopup(props: Readonly<AddEmployeeProtectMedicalNotePopupProps>): JSX.Element {
    const { getText } = useTranslation();

    const [selectedEmployeesList, setSelectedEmployeesList] = useState([]);
    const filteredEmployees = cacheStore
        .GetClinicians(false)
        .filter(person =>
            !selectedEmployeesList.any(employee => employee.ContactFacetId === person.ContactFacetId)
        && !props.contactFacetIds?.any(contactFacetId => contactFacetId === person.ContactFacetId)
        );

    const footer: JSX.Element = (
        <div className="p-footer">
            <div className="text-right">
                <ButtonControl
                    className="btn btn-default push-right-5"
                    onClick={() => props.onClose()}
                    label={getText("CancelLabel")}
                    dataCy={`${props.dataCy}-popup-cancel`}
                />
                <ButtonControl
                    variant="brand"
                    label={getText("AddLabel")}
                    onClick={() => props.onSave(selectedEmployeesList.map(employee => employee.ContactFacetId as Guid))}
                    disabled={!selectedEmployeesList.any()}
                    dataCy={`${props.dataCy}-popup-add`}
                />
            </div>
        </div>
    );

    return (
        <Modal
            title={getText("Allowlist_AddUsersLabel")}
            onClose={() => props.onClose()}
            disableOverlayClose
            footer={footer}
            showX
            showOverlay
            draggable
            fixedOverlayPosition
            style={{ overflow: "visible" }}
            bodyStyle={{ overflow: "visible" }}
            cssClass={styles.addEmployeeProtectMedicalNoteModal}
            dataCy={props.dataCy}
        >
            <div className={styles.addEmployeeProtectMedicalNotePopupBody}>
                <div className={styles.usersListField}>
                    <label className={styles.listOfUserLabel}>{getText("Allowlist_ListOfUsersLabel")}</label>
                    <UsersListFieldComponent
                        selectedEmployeesList={selectedEmployeesList}
                        setSelectedEmployees={setSelectedEmployeesList}
                        searchableEmployees={filteredEmployees
                            .map(person => new SearchableItemModelT<Person>(person.NameWithIdentifier, person.ContactFacetId, person))
                            .toImmutable()
                        }
                        dataCy={props.dataCy}
                        mode={FormMode.Edit}
                    />
                </div>
            </div>
        </Modal>
    );
}

export { AddEmployeeProtectMedicalNotePopup };
